import {
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	useLoaderData,
} from 'react-router'
import { ProgramMap } from '#app/components/organisms/program'
import { getVenuePinData } from '#app/domains/program/pure'
import { prisma } from '#app/utils/db.server.ts'
import { requireFestivalVisible } from '#app/utils/maintenance.server.ts'
import {
	formatClock,
	formatDayMonth,
	formatWeekday,
	groupEventsByDate,
	performersDisplay,
	splitByDays,
} from '#app/domains/program/pure'
import {
	getCurrentFestival,
	type EventWithSeatsAndTickets,
} from '#app/utils/providers/events.server.js'
import logoNoBackground from '../../assets/logo-no-background.png'
import programStyleUrl from '../../styles/program.css?url'
import { pageMeta } from '#app/utils/page-meta.ts'

export const meta: MetaFunction = (args) =>
	pageMeta(args, { title: 'Program (print)' })

export const links: LinksFunction = () => [
	{ rel: 'stylesheet', href: programStyleUrl },
]

const isTiered = (festival: { pricingModel: string } | null) =>
	festival ? festival.pricingModel === 'TIERED' : true

const formatDateHeading = (date: Date) =>
	`${formatWeekday(date)} ${formatDayMonth(date)}`

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const yearParam = url.searchParams.get('year')
	const year = yearParam ? Number.parseInt(yearParam, 10) : null

	const current = await getCurrentFestival()
	// every year's programme follows the site's gate: while the current
	// festival is closed (planning, maintenance, countdown), so are old years
	await requireFestivalVisible(request, current)

	const festival = Number.isFinite(year)
		? await prisma.festival.findFirst({
				where: { year: year! },
				include: {
					events: {
						include: { seats: true },
						orderBy: { date: 'asc' },
					},
				},
			})
		: current
	// and a year that isn't current keeps its own gate too
	if (festival && !festival.isCurrent) {
		await requireFestivalVisible(request, festival)
	}

	const events = (festival?.events ??
		[]) as unknown as EventWithSeatsAndTickets[]

	return {
		festival,
		events,
		grouped: Object.entries(groupEventsByDate(events)),
	}
}

type LoaderData = ReturnType<typeof useLoaderData<typeof loader>>

export default function ProgramPrint() {
	const { festival, events, grouped } = useLoaderData<LoaderData>()

	const contactName = festival?.contactName ?? 'Arne Skorpe Sjøen'
	const contactPhone = festival?.contactPhone ?? '41104852'
	const contactEmail = festival?.contactEmail ?? 'sjoen90@gmail.com'

	const [leftPanelDays, rightPanelDays] = splitByDays(grouped)
	const tiered = isTiered(festival)

	const pinNumbers: Record<string, number> = {}
	for (const pin of getVenuePinData(events)) {
		pinNumbers[pin.location.toLowerCase()] = pin.number
	}

	return (
		<main className="program-sheets">
			<div className="program-print-note">
				<h2 style={{ marginTop: 0 }}>Utskrift av program (tri-fold)</h2>
				<p>
					Skriv ut på A4 <strong>liggjande</strong>, dobbeltsidig,{' '}
					<strong>vend på kort kant</strong> («flip on short edge»), 100 % skala
					(ingen «fit to page»), marginar: ingen. Brett arket i tre
					(rullefalding): brett først høgre tredjedel inn over, deretter venstre
					tredjedel — då ligg framsida øvst. <kbd>/program</kbd> viser
					festivalen i år, <kbd>/program?year=2024</kbd> eit bestemt år.
				</p>
			</div>

			{/* ---------- Sheet A: outside ---------- */}
			<section className="program-sheet">
				{/* fold-in flap: practical info */}
				<div className="program-panel">
					<p className="pp-eyebrow">Praktisk informasjon</p>
					<h2 className="pp-title">Billettar</h2>
					<hr className="pp-rule" />
					{tiered ? (
						<ul className="pp-info-list">
							<li>Éin konsert: {festival?.priceOne ?? 200} kr</li>
							<li>To konsertar: {festival?.priceTwo ?? 300} kr</li>
							<li>Tre konsertar: {festival?.priceThree ?? 400} kr</li>
							<li>Fire konsertar: {festival?.priceFour ?? 500} kr</li>
							<li>
								<strong>Barn fram til 12 år går gratis.</strong>
							</li>
						</ul>
					) : (
						<ul className="pp-info-list">
							<li>Per konsert: {festival?.pricePerConcert ?? 200} kr</li>
							{festival?.festivalPassPrice ? (
								<li>
									Festivalpass (alle konsertar): {festival.festivalPassPrice} kr
								</li>
							) : null}
							<li>
								<strong>Barn fram til 12 år går gratis.</strong>
							</li>
						</ul>
					)}
					<div className="pp-section-gap">
						<h2 className="pp-title">Reservasjon og betaling</h2>
						<hr className="pp-rule" />
						<ul className="pp-info-list">
							<li>
								Billettar blir reserverte på{' '}
								<strong>huskonsertfestivalen.no</strong>
							</li>
							<li>Betaling med Vipps eller kontant i døra før konsertstart</li>
						</ul>
					</div>
					<div className="pp-section-gap">
						<h2 className="pp-title">Treng du hjelp?</h2>
						<hr className="pp-rule" />
						<ul className="pp-info-list">
							<li>
								Kontakt {contactName} for hjelp med reservasjon av billettar
							</li>
							<li>Tlf: {contactPhone}</li>
							<li>E-post: {contactEmail}</li>
						</ul>
					</div>
				</div>

				{/* back cover */}
				<div className="program-panel pp-back">
					<div>
						<hr className="pp-rule" />
						<div className="pp-back-block">
							<strong>Huskonsertfestivalen</strong> er ein festival for heile
							familien, arrangert av lokale musikantar.
						</div>
						<div className="pp-back-block">
							Takk til alle vertane på Vorrå som stiller husa sine til
							disposisjon, og til alle som kjem og fyller romma med musikk.
						</div>
						<ul className="pp-back-links">
							<li>
								<strong>Nett:</strong> huskonsertfestivalen.no
							</li>
							<li>
								<strong>Kontakt:</strong> {contactName}, {contactPhone}
							</li>
							<li>
								<strong>E-post:</strong> {contactEmail}
							</li>
						</ul>
						<hr className="pp-rule" />
						<p className="pp-cover-sub">Huskonsertfestivalen</p>
					</div>
				</div>

				{/* front cover */}
				<div className="program-panel pp-cover">
					<img
						src={logoNoBackground}
						alt="Huskonsertfestivalen"
						className="pp-cover-logo"
					/>
					<h1 className="pp-cover-title">Huskonsertfestivalen på Vorrå</h1>
					<p className="pp-cover-dates">{festival?.datesText}</p>
					<p className="pp-cover-sub">Program &amp; kart</p>
				</div>
			</section>

			{/* ---------- Sheet B: inside ---------- */}
			<section className="program-sheet">
				<div className="program-panel">
					<p className="pp-eyebrow">Program</p>
					{(leftPanelDays ?? []).map(([dateKey, dayEvents]) => (
						<div key={dateKey} style={{ marginBottom: '6mm' }}>
							<h3 className="pp-date-heading">
								{formatDateHeading(new Date(dateKey))}
							</h3>
							<p className="pp-date-sub">
								{dayEvents.length}{' '}
								{dayEvents.length === 1 ? 'konsert' : 'konsertar'}
							</p>
							{dayEvents.map((event) => (
								<ProgramEntry
									key={event.id}
									event={event}
									pinNumbers={pinNumbers}
								/>
							))}
						</div>
					))}
					{!events.length ? (
						<p>Det er ikkje lagt inn nokon konsertar enno.</p>
					) : null}
				</div>

				<div className="program-panel">
					{(rightPanelDays ?? []).map(([dateKey, dayEvents]) => (
						<div key={dateKey} style={{ marginBottom: '6mm' }}>
							<h3 className="pp-date-heading">
								{formatDateHeading(new Date(dateKey))}
							</h3>
							<p className="pp-date-sub">
								{dayEvents.length}{' '}
								{dayEvents.length === 1 ? 'konsert' : 'konsertar'}
							</p>
							{dayEvents.map((event) => (
								<ProgramEntry
									key={event.id}
									event={event}
									pinNumbers={pinNumbers}
								/>
							))}
						</div>
					))}
				</div>

				{/* map panel */}
				<div className="program-panel pp-map">
					<p className="pp-eyebrow">Kart</p>
					<figure className="pp-map-figure">
						<ProgramMap events={events} />
						<figcaption className="pp-map-caption">
							Kart over Vorrå-feltet. Tala svarer til lokala i programmet.
						</figcaption>
					</figure>
					<ul className="pp-map-legend">
						{getVenuePinData(events).map((pin) => (
							<li key={pin.number}>
								<span className="pp-pin">{pin.number}</span>
								<span>{pin.location}</span>
							</li>
						))}
					</ul>
				</div>
			</section>
		</main>
	)
}

function ProgramEntry({
	event,
	pinNumbers,
}: {
	event: EventWithSeatsAndTickets
	pinNumbers: Record<string, number>
}) {
	const date = new Date(event.date)
	return (
		<article className="pp-entry">
			<div className="pp-entry-top">
				<span className="pp-entry-time">{formatClock(date)}</span>
				<span className="pp-entry-title">{event.title}</span>
			</div>
			{(performersDisplay(event) ?? null) ? (
				<p className="pp-entry-performers">{performersDisplay(event)}</p>
			) : null}
			{event.shortDescription ? (
				<p className="pp-entry-summary">{event.shortDescription}</p>
			) : null}
			{event.location ? (
				<PinLocation location={event.location} pinNumbers={pinNumbers} />
			) : null}
		</article>
	)
}

function PinLocation({
	location,
	pinNumbers,
}: {
	location: string
	pinNumbers: Record<string, number>
}) {
	const addressPart = location
		.split(/\s+-\s+/)
		.slice(1)
		.join(' - ')
		.trim()
	const label = addressPart || location
	const number = pinNumbers[label.toLowerCase()]
	return (
		<div className="pp-entry-location">
			{number ? <span className="pp-pin">{number}</span> : null}
			<span>{label}</span>
		</div>
	)
}
